import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import cdoAI from '../cdo/cdoAI.js';
import cdoBeing from '../cdo/cdoBeing.js';
import { mediaSource } from '../shared/mediaSource';
import MediaImage from './MediaImage';

const STAT_FIELDS = [
  { key: 'strength', label: 'Strength', help: 'Raw force, lifting power, weapon impact, and physical pressure.' },
  { key: 'agility', label: 'Agility', help: 'Balance, reflexes, fine motion, dodging, and quick physical response.' },
  { key: 'constitution', label: 'Constitution', help: 'Endurance, resilience, stamina, disease resistance, and pain tolerance.' },
  { key: 'intellect', label: 'Intellect', help: 'Study, memory, reason, craft knowledge, and magical learning.' },
  { key: 'wisdom', label: 'Wisdom', help: 'Judgment, awareness, patience, inner discipline, and spiritual channeling.' },
  { key: 'charisma', label: 'Charisma', help: 'Presence, persuasion, leadership, confidence, and social force.' },
];

const STAT_MIN = -5;
const STAT_MAX = 30;
const STAT_POINT_TOTAL = 75;
const PRIMARY_SKILL_MIN = 0;
const PRIMARY_SKILL_MAX = 4;
const PRIMARY_SKILL_POINT_TOTAL = 18;
const MAX_IMAGE_CANDIDATES = 5;
const MAX_ANCESTOR_REGENERATIONS = 2;
const FAMILY_AI_STEP_TOTAL = 14;
const CURRENT_AGE_ID = 503;
const CURRENT_YEAR = 1000;
const CURRENT_SEASON_ID = 513;
const CURRENT_DAY = 1;

const backgroundCacheKey = id => `adenrel.character-creation.background.v1.${id}`;
const readBackgroundCache = id => {
  try {
    const cached = JSON.parse(localStorage.getItem(backgroundCacheKey(id)));
    if (cached?.characterId === id && cached.familyRolls && Array.isArray(cached.ancestors) && Array.isArray(cached.giftItems)
      && cached.ancestors.every(ancestor => FAMILY_ROLES.some(role => role.key === ancestor?.roleKey))
      && cached.giftItems.every(item => FAMILY_ROLES.some(role => role.key === item?.giverRoleKey))) return cached;
  } catch { /* Browser storage may be unavailable or contain an old draft. */ }
  return null;
};

const AGE_RANGES_BY_FORM = {
  elf: { base: 250, variance: 50 },
  dwarf: { base: 50, variance: 10 },
  gnome: { base: 50, variance: 10 },
  halfling: { base: 40, variance: 8 },
  hobbit: { base: 40, variance: 8 },
  human: { base: 21, variance: 3 },
};

const PRIMARY_ABILITY_DESCRIPTIONS = {
  1: 'Reduces damage, poison, and disease effects',
  2: 'Improves fighting and defense',
  3: 'Improves movement, travel, and endurance',
  4: 'Improves stalking, hiding, and trap understanding',
  5: 'Improves observation and attention to detail',
  6: 'Improves ability to acquire, attune, and cast spells',
  7: 'Improves ability to focus, heal, and sleep',
  8: 'Improves knowledge of the world',
  9: 'Improves ability to inspire, convince, and barter',
};

const FAMILY_ROLES = [
  { key: 'father', label: 'Father' },
  { key: 'mother', label: 'Mother' },
  { key: 'paternalGrandfather', label: 'Paternal Grandfather' },
  { key: 'paternalGrandmother', label: 'Paternal Grandmother' },
  { key: 'maternalGrandfather', label: 'Maternal Grandfather' },
  { key: 'maternalGrandmother', label: 'Maternal Grandmother' },
];

const rollTables = {
  level: [
    [1, 10, 'Level 1', { level: 1 }],
    [11, 20, 'Level 2', { level: 2 }],
    [21, 35, 'Level 3', { level: 3 }],
    [35, 50, 'Level 4', { level: 4 }],
    [51, 75, 'Level 5', { level: 5 }],
    [76, 90, 'Level 6', { level: 6 }],
    [76, 90, 'Level 7', { level: 7 }],
    [76, 90, 'Level 8', { level: 8 }],
    [91, 97, 'Level 9', { level: 9 }],
    [98, 100, 'Level 10', { level: 10 }],
  ],
  socialClass: [
    [1, 12, 'Destitute'],
    [13, 32, 'Laboring'],
    [33, 58, 'Common'],
    [59, 78, 'Skilled trade'],
    [79, 91, 'Comfortable'],
    [92, 98, 'Gentry'],
    [99, 100, 'Minor nobility'],
  ],
  maritalStatus: [
    [1, 12, 'Never married'],
    [13, 60, 'Married'],
    [61, 74, 'Widowed'],
    [75, 86, 'Separated'],
    [87, 96, 'Complicated household'],
    [97, 100, 'Secret or disputed marriage'],
  ],
  guardianStatus: [
    [1, 42, 'Present guardian'],
    [43, 62, 'Distant but known'],
    [63, 78, 'Absent by necessity'],
    [79, 90, 'Replaced by another guardian'],
    [91, 100, 'Unknown or contested'],
  ],
  livingStatus: [
    [1, 68, 'Living'],
    [69, 82, 'Dead'],
    [83, 92, 'Missing'],
    [93, 100, 'Rumored alive'],
  ],
  occupation: [
    [1, 10, 'Farmer or herder'],
    [11, 20, 'Craft worker'],
    [21, 30, 'Trader or factor'],
    [31, 40, 'Guard, soldier, or adventurer'],
    [41, 50, 'Scholar or scribe'],
    [51, 60, 'Priest, healer, or keeper'],
    [61, 70, 'Wanderer, scout, or guide'],
    [71, 80, 'Performer or storyteller'],
    [81, 90, 'Magical or occult worker'],
    [91, 100, 'Local office holder'],
  ],
  reputation: [
    [1, 14, 'Disgraced'],
    [15, 34, 'Quiet'],
    [35, 56, 'Dependable'],
    [57, 72, 'Admired'],
    [73, 86, 'Feared or stern'],
    [87, 96, 'Controversial'],
    [97, 100, 'Locally famous'],
  ],
  warmth: [
    [1, 16, 'Cold'],
    [17, 34, 'Distant'],
    [35, 62, 'Complicated'],
    [63, 84, 'Warm'],
    [85, 96, 'Devoted'],
    [97, 100, 'Defining bond'],
  ],
  notableTitle: [
    [1, 72, 'No inherited title'],
    [73, 90, 'Professional title'],
    [91, 97, 'Local title'],
    [98, 100, 'Important family title'],
  ],
  familyObligation: [
    [1, 18, 'Debt'],
    [19, 35, 'Oath'],
    [36, 52, 'Unfinished journey'],
    [53, 68, 'Protected secret'],
    [69, 84, 'Lost property'],
    [85, 100, 'Old warning'],
  ],
  giftDate: [
    [1, 22, 'Childhood parting', { ageAtGift: 8, eventTimeDay: 4 }],
    [23, 46, 'First apprenticeship', { ageAtGift: 13, eventTimeDay: 11 }],
    [47, 68, 'Coming of age', { ageAtGift: 16, eventTimeDay: 16 }],
    [69, 86, 'Before leaving home', { yearsBeforeStart: 1, eventTimeDay: 20 }],
    [87, 100, 'Last private meeting', { yearsBeforeStart: 0, eventTimeDay: CURRENT_DAY }],
  ],
};

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toDataImage = (response) => mediaSource(response);

const formatErrorMessage = (err, response) => {
  const source = response?.error ?? response ?? err;
  if (typeof source === 'string') {
    return source;
  }
  if (!source || typeof source !== 'object') {
    return 'Request failed.';
  }
  return source.message || source.sqlMessage || source.error || JSON.stringify(source);
};

const callCdo = (fn, ...args) =>
  new Promise((resolve, reject) => {
    fn(...args, (err, response) => {
      if (err || response?.error) {
        reject(new Error(formatErrorMessage(err, response)));
        return;
      }

      resolve(response);
    });
  });

const rollDie = () => Math.floor(Math.random() * 100) + 1;

const rollOnTable = (table) => {
  const roll = rollDie();
  const row = table.find(([low, high]) => roll >= low && roll <= high) ?? table[table.length - 1];
  return {
    roll,
    label: row[2],
    data: row[3] ?? {},
  };
};

const getAgeRangeForForm = (form) => {
  const key = String(form ?? '').trim().toLowerCase();
  return AGE_RANGES_BY_FORM[key] ?? null;
};

const generateDefaultAgeForForm = (form) => {
  const range = getAgeRangeForForm(form);
  if (!range) {
    return '';
  }
  return range.base + Math.floor(Math.random() * (range.variance * 2 + 1)) - range.variance;
};

const isAgeInDefaultRange = (form, age) => {
  const range = getAgeRangeForForm(form);
  const parsedAge = Number(age);
  if (!range || !Number.isFinite(parsedAge)) {
    return false;
  }
  return parsedAge >= range.base - range.variance && parsedAge <= range.base + range.variance;
};

const getAgeBoundsForForm = (form) => {
  const range = getAgeRangeForForm(form);
  if (!range) {
    return null;
  }

  return {
    min: range.base - range.variance,
    max: range.base + range.variance,
  };
};

const getCharacterAge = (character) => {
  const explicitAge = Number(character?.age);
  if (Number.isFinite(explicitAge) && explicitAge > 0) {
    return Math.round(explicitAge);
  }

  const createYear = Number(character?.createDateYear);
  if (Number.isFinite(createYear) && createYear > 0) {
    return Math.max(1, CURRENT_YEAR - createYear);
  }

  return 0;
};

const buildCharacterBirthDate = (age) => {
  const parsedAge = Number(age);
  if (!Number.isFinite(parsedAge) || parsedAge <= 0) {
    return {
      createDateAge: CURRENT_AGE_ID,
      createDateYear: '',
      createDateSeason: CURRENT_SEASON_ID,
      createDateDay: CURRENT_DAY,
    };
  }

  return {
    createDateAge: CURRENT_AGE_ID,
    createDateYear: CURRENT_YEAR - Math.round(parsedAge),
    createDateSeason: CURRENT_SEASON_ID,
    createDateDay: CURRENT_DAY,
  };
};

const buildGiftDate = (giftDateRoll, characterAge) => {
  const age = Math.max(1, Number(characterAge) || 1);
  const data = giftDateRoll?.data ?? {};
  const giftAge = Number.isFinite(Number(data.ageAtGift))
    ? Math.min(age, Math.max(1, Number(data.ageAtGift)))
    : Math.max(1, age - Math.max(0, Number(data.yearsBeforeStart) || 0));

  return {
    eventTimeAge: CURRENT_AGE_ID,
    eventTimeYear: CURRENT_YEAR - age + giftAge,
    eventTimeSeason: CURRENT_SEASON_ID,
    eventTimeDay: Number.isFinite(Number(data.eventTimeDay)) ? Number(data.eventTimeDay) : CURRENT_DAY,
    label: giftDateRoll?.label ?? '',
  };
};

const rollFamilyMember = (roleKey, characterAge = 0) => {
  const giftDate = rollOnTable(rollTables.giftDate);
  return {
    roleKey,
    level: rollOnTable(rollTables.level),
    socialClass: rollOnTable(rollTables.socialClass),
    maritalStatus: rollOnTable(rollTables.maritalStatus),
    guardianStatus: rollOnTable(rollTables.guardianStatus),
    livingStatus: rollOnTable(rollTables.livingStatus),
    occupation: rollOnTable(rollTables.occupation),
    reputation: rollOnTable(rollTables.reputation),
    warmth: rollOnTable(rollTables.warmth),
    notableTitle: rollOnTable(rollTables.notableTitle),
    familyObligation: rollOnTable(rollTables.familyObligation),
    giftDate: {
      ...giftDate,
      data: {
        ...buildGiftDate(giftDate, characterAge),
      },
    },
  };
};

const buildFamilyRolls = (characterAge = 0) =>
  FAMILY_ROLES.reduce((rolls, role) => ({
    ...rolls,
    [role.key]: rollFamilyMember(role.key, characterAge),
  }), {});

const buildGiftBias = (character) => {
  const intellect = numberValue(character?.intellect);
  const wisdom = numberValue(character?.wisdom);
  const charisma = numberValue(character?.charisma);
  if (intellect >= 15 && wisdom >= 15 && charisma >= 15) {
    return 'Psionics';
  }
  if (intellect >= 15) {
    return 'Wizardry';
  }
  return 'None';
};

function HelpLabel({ label, help }) {
  const helpId = useId();

  return (
    <span className="creation-label-row">
      <span>{label}</span>
      <span className="creation-help-wrap">
        <button type="button" className="creation-help-button" aria-label={`${label} help`} aria-describedby={helpId}>
          i
        </button>
        <span id={helpId} className="creation-help-tooltip" role="tooltip">
          {help}
        </span>
      </span>
    </span>
  );
}

function CharacterCreation({
  value,
  onChange,
  onSaveDraft,
  onComplete,
  onExit,
  isLoading = false,
  isSaving = false,
  isWaitingForWorld = false,
  errorMessage = '',
  statusMessage = '',
  masterTypes = [],
  primarySkillOptions = [],
  isGeneratingDescription = false,
  onGenerateDescription,
}) {
  const character = useMemo(() => value ?? {}, [value]);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState('');
  const [page2Status, setPage2Status] = useState('');
  const [runError, setRunError] = useState('');
  const [isFinalizing, setIsFinalizing] = useState(false);
  const finalizingRef = useRef(false);
  const [generationErrors, setGenerationErrors] = useState({});
  const [familyRolls, setFamilyRolls] = useState(() => buildFamilyRolls(getCharacterAge(character)));
  const [ancestors, setAncestors] = useState([]);
  const [giftItems, setGiftItems] = useState([]);
  const [backstory, setBackstory] = useState(null);
  const [portraitCandidates, setPortraitCandidates] = useState([]);
  const [displayedPortraitIndex, setDisplayedPortraitIndex] = useState(0);
  const [imageAttempts, setImageAttempts] = useState(0);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isSettingPrimaryImage, setIsSettingPrimaryImage] = useState(false);
  const [isGeneratingFamily, setIsGeneratingFamily] = useState(false);
  const [progressCompleted, setProgressCompleted] = useState(0);
  const [progressTotal, setProgressTotal] = useState(FAMILY_AI_STEP_TOTAL);
  const [regenerateCount, setRegenerateCount] = useState(0);
  const page2StartedForRef = useRef('');
  const runIdRef = useRef(0);
  const requestHandlesRef = useRef([]);
  const [cachedCharacterId, setCachedCharacterId] = useState(null);

  useEffect(() => {
    if (!cachedCharacterId || cachedCharacterId !== character.idEntity) return;
    try {
      localStorage.setItem(backgroundCacheKey(cachedCharacterId), JSON.stringify({
        characterId: cachedCharacterId, familyRolls, ancestors, giftItems, backstory, regenerateCount,
      }));
    } catch { /* Keep the current in-memory draft when browser storage is full. */ }
  }, [cachedCharacterId, character.idEntity, familyRolls, ancestors, giftItems, backstory, regenerateCount]);

  const primarySkills = useMemo(
    () => (Array.isArray(character.primarySkills) ? character.primarySkills : []),
    [character.primarySkills]
  );
  const statTotal = STAT_FIELDS.reduce((total, stat) => total + numberValue(character[stat.key]), 0);
  const statRemaining = STAT_POINT_TOTAL - statTotal;
  const primarySkillTotal = primarySkills.reduce((total, skill) => total + numberValue(skill.level), 0);
  const primarySkillRemaining = PRIMARY_SKILL_POINT_TOTAL - primarySkillTotal;
  const characterAge = getCharacterAge(character);
  const ageBounds = getAgeBoundsForForm(character.beingType);
  const giftBias = buildGiftBias(character);
  const displayedPortrait = portraitCandidates[displayedPortraitIndex] ?? null;
  const displayedPortraitIsPrimary = Boolean(displayedPortrait?.isPrimary);
  const canRegenerateImage = imageAttempts < MAX_IMAGE_CANDIDATES && !isGeneratingImage && Boolean(character.idEntity);
  const canRegenerateFamily =
    regenerateCount < MAX_ANCESTOR_REGENERATIONS && !isGeneratingFamily && !isGeneratingImage && Boolean(character.idEntity);
  const isPage2Ready = Boolean(
    !isGeneratingFamily &&
    !isGeneratingImage &&
    backstory?.backstory &&
    displayedPortrait
  );

  const getMasterTypeOptions = useCallback(
    (column) =>
      masterTypes
        .filter((masterType) => masterType.column === column)
        .map((masterType) => ({
          value: masterType.value,
          label: masterType.value,
        })),
    [masterTypes]
  );

  const characterBeingTypeOptions = useMemo(() => {
    const allowedBeingTypeOrder = ['elf', 'dwarf', 'human', 'halfling', 'gnome'];
    return getMasterTypeOptions('beingType')
      .filter((option) => allowedBeingTypeOrder.includes(String(option.value ?? '').trim().toLowerCase()))
      .sort(
        (left, right) =>
          allowedBeingTypeOrder.indexOf(String(left.value ?? '').trim().toLowerCase()) -
          allowedBeingTypeOrder.indexOf(String(right.value ?? '').trim().toLowerCase())
      );
  }, [getMasterTypeOptions]);

  const displaySkillRows = useMemo(
    () =>
      primarySkillOptions.map((skillOption) => {
        const existing = primarySkills.find(
          (skill) => Number(skill.idPrimarySkillList) === Number(skillOption.idPrimarySkillList)
        );
        return {
          ...skillOption,
          level: numberValue(existing?.level),
        };
      }),
    [primarySkillOptions, primarySkills]
  );

  const updateCharacter = (patch) => {
    const nextAge = patch.age ?? character.age;
    onChange({
      ...character,
      ...patch,
      entityType: 'Being',
      level: 1,
      defaultForm: patch.beingType ?? patch.defaultForm ?? character.defaultForm ?? '',
      alignment: null,
      ...(['age', 'beingType', 'defaultForm'].some((field) => Object.prototype.hasOwnProperty.call(patch, field))
        ? buildCharacterBirthDate(nextAge)
        : {}),
      multipleForms: false,
      personality: '',
      objectivesShortTerm: '',
      objectivesLongTerm: '',
      forms: [],
      relationships: [],
      itemSuggestions: [],
    });
  };

  const updateBeingType = (nextValue) => {
    const shouldSetDefaultAge =
      !characterAge || isAgeInDefaultRange(character.beingType, characterAge);
    const nextAge = shouldSetDefaultAge ? generateDefaultAgeForForm(nextValue) : characterAge;
    updateCharacter({
      beingType: nextValue,
      defaultForm: nextValue,
      ...(nextAge ? { age: nextAge } : {}),
    });
  };

  const adjustAge = (delta) => {
    const bounds = getAgeBoundsForForm(character.beingType);
    if (!bounds) {
      return;
    }

    const current = characterAge || generateDefaultAgeForForm(character.beingType);
    const next = Math.min(bounds.max, Math.max(bounds.min, current + delta));
    updateCharacter({ age: next });
  };

  const adjustStat = (field, delta) => {
    const current = numberValue(character[field]);
    const next = current + delta;
    if (next < STAT_MIN || next > STAT_MAX) {
      return;
    }
    if (delta > 0 && statTotal >= STAT_POINT_TOTAL) {
      return;
    }
    updateCharacter({ [field]: next });
  };

  const adjustPrimarySkill = (idPrimarySkillList, delta) => {
    const nextSkills = primarySkillOptions.map((skillOption) => {
      const existing = primarySkills.find(
        (skill) => Number(skill.idPrimarySkillList) === Number(skillOption.idPrimarySkillList)
      );
      const currentLevel = numberValue(existing?.level);
      const nextLevel =
        Number(skillOption.idPrimarySkillList) === Number(idPrimarySkillList)
          ? currentLevel + delta
          : currentLevel;

      return {
        idPrimarySkill: existing?.idPrimarySkill ?? '',
        idPrimarySkillList: skillOption.idPrimarySkillList,
        name: skillOption.name,
        stat: skillOption.stat,
        description: skillOption.description ?? '',
        gmNotes: skillOption.gmNotes ?? '',
        level: Math.min(PRIMARY_SKILL_MAX, Math.max(PRIMARY_SKILL_MIN, nextLevel)),
      };
    });

    const nextTotal = nextSkills.reduce((total, skill) => total + numberValue(skill.level), 0);
    if (nextTotal > PRIMARY_SKILL_POINT_TOTAL) {
      return;
    }
    updateCharacter({ primarySkills: nextSkills });
  };

  const getMissingPage1Requirements = () => {
    const missing = [];
    if (!String(character.name ?? '').trim()) {
      missing.push('name');
    }
    if (!String(character.beingType ?? '').trim()) {
      missing.push('form');
    }
    if (!String(character.gender ?? '').trim()) {
      missing.push('gender');
    }
    if (!characterAge || characterAge <= 0) {
      missing.push('starting age');
    } else if (ageBounds && (characterAge < ageBounds.min || characterAge > ageBounds.max)) {
      missing.push(`starting age between ${ageBounds.min} and ${ageBounds.max}`);
    }
    if (!String(character.description ?? '').trim()) {
      missing.push('description');
    }
    if (statRemaining !== 0) {
      missing.push(`${STAT_POINT_TOTAL} stat points allotted`);
    }
    if (primarySkillRemaining !== 0) {
      missing.push(`${PRIMARY_SKILL_POINT_TOTAL} primary ability points allotted`);
    }
    return missing;
  };

  const registerRequest = useCallback((xhr) => {
    if (xhr && typeof xhr.abort === 'function') {
      requestHandlesRef.current.push(xhr);
    }
    return xhr;
  }, []);

  const callAbortable = useCallback(
    (fn, ...args) =>
      new Promise((resolve, reject) => {
        const xhr = fn(...args, (err, response) => {
          requestHandlesRef.current = requestHandlesRef.current.filter((entry) => entry !== xhr);
          if (err || response?.error) {
            reject(new Error(formatErrorMessage(err, response)));
            return;
          }
          resolve(response);
        });
        registerRequest(xhr);
      }),
    [registerRequest]
  );

  const abortActiveRequests = () => {
    requestHandlesRef.current.forEach((xhr) => {
      try {
        xhr.abort();
      } catch {
        // Stale request cleanup is best effort.
      }
    });
    requestHandlesRef.current = [];
  };

  const incrementProgress = (runId) => {
    if (runIdRef.current !== runId) {
      return;
    }
    setProgressCompleted((current) => current + 1);
  };

  const buildPortraitPrompt = useCallback(() => {
    const statSummary = STAT_FIELDS.map((field) => `${field.label}: ${numberValue(character?.[field.key])}`).join(', ');
    const abilitySummary = displaySkillRows
      .filter((skill) => numberValue(skill.level) > 0)
      .map((skill) => `${skill.name}: ${numberValue(skill.level)}`)
      .join(', ');
    const familySummary = ancestors
      .map((ancestor) => {
        const roleLabel = FAMILY_ROLES.find((role) => role.key === ancestor.roleKey)?.label || 'Ancestor';
        const nameLabel = ancestor.name ? ` (${ancestor.name})` : '';
        return `${roleLabel}${nameLabel}: ${ancestor.description}`;
      })
      .join('\n');

    return `Create a square 1:1 portrait image of this Adenrel first-level player character.

Name: ${character.name || 'Unnamed'}
Form: ${character.beingType || 'Human'}
Gender: ${character.gender || 'Unspecified'}
Age: ${characterAge || 'Unspecified'}
Heraldry / Colors: ${character.heraldry || 'None'}
Description: ${character.description || 'A first-level adventurer.'}
Stats: ${statSummary}
Primary abilities: ${abilitySummary || 'Unassigned'}
Family context:
${familySummary || 'No generated ancestry yet.'}
Backstory:
${backstory?.backstory || 'No generated backstory yet.'}

Visual requirements:
- Show the character clearly as the main subject.
- Keep the character grounded as a beginning adventurer.
- Use heraldry, family context, and backstory only as visual inspiration for identity, posture, expression, colors, and mood.
- This is the initial creation portrait before adventuring items are equipped: do not show family gifts, heirlooms, significant weapons, shields, armor, packs, treasure, magical items, or lots of gear.
- Keep visible equipment minimal and ordinary, such as simple travel clothing, a plain cloak, or one small mundane accessory if needed.
- Do not include text, labels, UI, logos, or watermarks.`;
  }, [ancestors, backstory?.backstory, character, characterAge, displaySkillRows]);

  const loadSavedPortraitCandidates = useCallback(async () => {
    if (!character.idEntity) {
      return [];
    }

    const rows = await callCdo(cdoBeing.selectPortraitImages.bind(cdoBeing), character.idEntity);
    const candidates = (Array.isArray(rows) ? rows : [])
      .filter((row) => toDataImage(row))
      .map((row) => ({
        ...row,
        src: toDataImage(row),
        isPrimary: Boolean(row.isPrimary),
      }));

    setPortraitCandidates(candidates);
    setImageAttempts(candidates.length);
    const primaryIndex = candidates.findIndex((candidate) => candidate.isPrimary);
    setDisplayedPortraitIndex(primaryIndex >= 0 ? primaryIndex : 0);
    return candidates;
  }, [character.idEntity]);

  const generatePortraitCandidate = useCallback(async (runId, options = {}) => {
    if (!character.idEntity || isGeneratingImage) {
      return null;
    }
    if (!options.ignoreLimit && imageAttempts >= MAX_IMAGE_CANDIDATES) {
      setRunError(`Image regeneration is limited to ${MAX_IMAGE_CANDIDATES} total portraits.`);
      return null;
    }

    setIsGeneratingImage(true);
    setGenerationErrors((current) => ({ ...current, portrait: '' }));
    setRunError('');
    setPage2Status(options.regenerate ? 'Regenerating character portrait...' : 'Generating character portrait...');

    try {
      const nextAttempt = imageAttempts + 1;
      const image = await callAbortable(cdoAI.generateImage.bind(cdoAI), {
        beingId: character.idEntity,
        aogYear: 0,
        size: '1024x1024',
        prompt: buildPortraitPrompt(),
      });

      if (runIdRef.current !== runId) {
        return null;
      }

      if (!image?.imageBase64) {
        throw new Error('Image generation returned no image data.');
      }

      const saved = await callAbortable(cdoBeing.savePortraitCandidate.bind(cdoBeing), {
        idEntity: character.idEntity,
        name: character.name,
        title: character.name,
        variantKey: `candidate-${nextAttempt}`,
        mediaType: image.mimeType || 'image/png',
        imageBase64: image.imageBase64,
        revisedPrompt: image.revisedPrompt || '',
        setPrimary: false,
      });

      if (runIdRef.current !== runId) {
        return null;
      }

      const candidate = {
        ...saved,
        src: toDataImage(saved),
        isPrimary: Boolean(saved?.isPrimary),
      };
      setPortraitCandidates((current) => [...current, candidate]);
      setDisplayedPortraitIndex(nextAttempt - 1);
      setImageAttempts(nextAttempt);
      if (!options.regenerate) {
        incrementProgress(runId);
      }
      return candidate;
    } catch (error) {
      if (runIdRef.current === runId) {
        setGenerationErrors((current) => ({ ...current, portrait: error?.message || 'Failed to generate portrait.' }));
      }
      return null;
    } finally {
      if (runIdRef.current === runId) {
        setIsGeneratingImage(false);
      }
    }
  }, [callAbortable, character.idEntity, character.name, imageAttempts, isGeneratingImage, buildPortraitPrompt]);

  const runFamilyGeneration = useCallback(async (runId, rolls, completed = {}) => {
    if (!character.idEntity) {
      return;
    }

    setIsGeneratingFamily(true);
    setGenerationErrors((current) => ({ ...current, background: '' }));
    setPage2Status('Generating family background...');

    try {
      const nextAncestors = [...(completed.ancestors || [])];
      for (const role of FAMILY_ROLES) {
        if (nextAncestors.some((ancestor) => ancestor.roleKey === role.key)) continue;
        const ancestor = await callAbortable(cdoAI.generateCharacterAncestor.bind(cdoAI), {
          roleKey: role.key,
          roleLabel: role.label,
          character,
          rolls: rolls[role.key],
          existingAncestors: nextAncestors,
        });
        if (runIdRef.current !== runId) {
          return;
        }
        nextAncestors.push(ancestor);
        setAncestors([...nextAncestors]);
        incrementProgress(runId);
      }

      const nextItems = [...(completed.giftItems || [])];
      for (const ancestor of nextAncestors) {
        if (nextItems.some((item) => item.giverRoleKey === ancestor.roleKey)) continue;
        const item = await callAbortable(cdoAI.generateCharacterGiftItem.bind(cdoAI), {
          itemKey: `gift-${ancestor.roleKey}`,
          giverRoleKey: ancestor.roleKey,
          character,
          giver: ancestor,
          ancestors: nextAncestors,
          existingGiftItems: nextItems,
          giftBias,
          rolls: rolls[ancestor.roleKey],
        });
        if (runIdRef.current !== runId) {
          return;
        }
        nextItems.push(item);
        setGiftItems([...nextItems]);
        incrementProgress(runId);
      }

      const nextBackstory = completed.backstory?.backstory ? completed.backstory
        : await callAbortable(cdoAI.generateCharacterBackstory.bind(cdoAI), {
          character,
          ancestors: nextAncestors,
          items: nextItems,
        });
      if (runIdRef.current !== runId) {
        return;
      }
      setBackstory(nextBackstory);
      if (!completed.backstory?.backstory) incrementProgress(runId);
      setPage2Status('Background ready.');
    } catch (error) {
      if (runIdRef.current === runId) {
        setGenerationErrors((current) => ({ ...current, background: error?.message || 'Failed to generate character background.' }));
        setPage2Status('Background generation failed. Retry below.');
      }
    } finally {
      if (runIdRef.current === runId) {
        setIsGeneratingFamily(false);
      }
    }
  }, [callAbortable, character, giftBias]);

  const startInitialPage2 = useCallback(async () => {
    if (!character.idEntity || page2StartedForRef.current === character.idEntity) {
      return;
    }
    page2StartedForRef.current = character.idEntity;
    const cached = readBackgroundCache(character.idEntity);
    const rolls = cached?.familyRolls || familyRolls;
    if (cached) {
      setFamilyRolls(rolls);
      setAncestors(cached.ancestors);
      setGiftItems(cached.giftItems);
      setBackstory(cached.backstory || null);
      setRegenerateCount(Math.min(MAX_ANCESTOR_REGENERATIONS, Math.max(0, Number(cached.regenerateCount) || 0)));
    }
    setCachedCharacterId(character.idEntity);
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    setProgressCompleted((cached?.ancestors.length || 0) + (cached?.giftItems.length || 0) + (cached?.backstory?.backstory ? 1 : 0));
    setProgressTotal(FAMILY_AI_STEP_TOTAL);
    setRunError('');
    setPage2Status('Preparing background and image...');

    const savedCandidates = await loadSavedPortraitCandidates().catch(() => []);
    if (runIdRef.current !== runId) {
      return;
    }

    const portraitPromise = savedCandidates.length === 0
      ? generatePortraitCandidate(runId, { ignoreLimit: true })
      : Promise.resolve(savedCandidates[0]);
    if (savedCandidates.length > 0) {
      incrementProgress(runId);
    }
    const familyPromise = runFamilyGeneration(runId, rolls, cached || {});
    await Promise.allSettled([portraitPromise, familyPromise]);
  }, [character.idEntity, familyRolls, generatePortraitCandidate, loadSavedPortraitCandidates, runFamilyGeneration]);

  useEffect(() => {
    if (page === 2) {
      startInitialPage2();
    }
  }, [page, startInitialPage2]);

  useEffect(() => () => abortActiveRequests(), []);

  const handlePage1Continue = async () => {
    const missing = getMissingPage1Requirements();
    if (missing.length > 0) {
      setMessage(`Continue needs ${missing.join(', ')}.`);
      return;
    }

    setMessage('');
    try {
      await onSaveDraft?.({
        ...character,
        alignment: null,
        age: characterAge,
        ...buildCharacterBirthDate(characterAge),
      });
      if (!page2StartedForRef.current) setFamilyRolls(buildFamilyRolls(characterAge));
      setPage(2);
    } catch (error) {
      setMessage(error?.message || 'Unable to save the character draft.');
    }
  };

  const handleRegenerateFamily = () => {
    if (!canRegenerateFamily) {
      return;
    }

    abortActiveRequests();
    const runId = runIdRef.current + 1;
    const nextRolls = buildFamilyRolls(characterAge);
    runIdRef.current = runId;
    setFamilyRolls(nextRolls);
    setAncestors([]);
    setGiftItems([]);
    setBackstory(null);
    setProgressCompleted(0);
    setProgressTotal(FAMILY_AI_STEP_TOTAL - 1);
    setRunError('');
    setRegenerateCount((current) => current + 1);
    setGenerationErrors((current) => ({ ...current, background: '' }));
    runFamilyGeneration(runId, nextRolls);
  };

  const handleRegenerateImage = () => {
    if (!canRegenerateImage) {
      return;
    }
    const runId = runIdRef.current;
    generatePortraitCandidate(runId, { regenerate: true });
  };

  const handleSetPrimaryImage = async () => {
    if (!displayedPortrait || displayedPortraitIsPrimary || isSettingPrimaryImage) {
      return displayedPortrait;
    }

    setIsSettingPrimaryImage(true);
    try {
      const saved = await callCdo(cdoBeing.setPrimaryPortraitImage.bind(cdoBeing), {
        idEntity: character.idEntity,
        variantKey: displayedPortrait.variantKey,
        idEntityMedia: displayedPortrait.idEntityMedia,
      });
      const selectedId = saved?.idEntityMedia ?? displayedPortrait.idEntityMedia;
      setPortraitCandidates((current) =>
        current.map((candidate) => ({
          ...candidate,
          isPrimary: candidate.idEntityMedia === selectedId,
        }))
      );
      return {
        ...displayedPortrait,
        isPrimary: true,
      };
    } finally {
      setIsSettingPrimaryImage(false);
    }
  };

  const handleFinalContinue = async () => {
    if (finalizingRef.current) return;
    if (!isPage2Ready) {
      setRunError('Continue needs a generated image and completed backstory.');
      return;
    }

    finalizingRef.current = true;
    setIsFinalizing(true);
    setRunError('');
    try {
      const selectedPortrait = await handleSetPrimaryImage();
      const result = await callCdo(cdoBeing.finalizeCharacterCreation.bind(cdoBeing), {
        idEntity: character.idEntity,
        ancestors,
        giftItems,
        backstory,
        selectedGifts: backstory?.selectedGifts ?? [],
        primaryPortraitVariantKey: selectedPortrait?.variantKey,
        primaryPortraitMediaId: selectedPortrait?.idEntityMedia,
      });
      // Persisted relatives/backstory now belong to the saved character.
      setCachedCharacterId(null);
      try { localStorage.removeItem(backgroundCacheKey(character.idEntity)); } catch { /* Best effort. */ }
      onComplete?.({
        character: result?.character,
        portrait: selectedPortrait,
      });
    } catch (error) {
      setRunError(error?.message || 'Unable to finalize character creation.');
    } finally {
      finalizingRef.current = false;
      setIsFinalizing(false);
    }
  };

  const progressPercent = Math.round((Math.min(progressCompleted, progressTotal) / Math.max(1, progressTotal)) * 100);

  if (page === 2) {
    return (
      <main className="character-sheet-page character-creation-page">
        <section className="character-sheet-shell character-creation-shell">
          <header className="character-sheet-header">
            <div>
              <h1>Background and Image</h1>
              <p>{character.name || 'New character'}</p>
            </div>
            <div className="character-level-pill">Level 1</div>
          </header>

          <section className="creation-progress-panel" aria-label="Generation progress">
            <div className="creation-progress-meta">
              <span>{page2Status || 'Preparing...'}</span>
              <strong>{progressPercent}%</strong>
            </div>
            <div className="creation-progress-bar">
              <span style={{ width: `${progressPercent}%` }} />
            </div>
          </section>

          <div className="creation-background-layout">
            <section className="character-sheet-section creation-family-section">
              <div className="character-sheet-section-header">
                <h2>Family Tree</h2>
                <button type="button" className="character-inline-button" onClick={handleRegenerateFamily} disabled={!canRegenerateFamily}>
                  Regenerate Ancestors and Backstory
                </button>
              </div>
              <div className="creation-family-grid">
                {FAMILY_ROLES.map((role) => {
                  const ancestor = ancestors.find((entry) => entry.roleKey === role.key);
                  return (
                    <article key={role.key} className="creation-family-card">
                      <span>{role.label}</span>
                      {ancestor ? (
                        <>
                          <h3>{ancestor.name}</h3>
                          <p>{ancestor.description}</p>
                        </>
                      ) : (
                        <p>{isGeneratingFamily ? 'Generating...' : 'Not generated yet.'}</p>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>

            <div className="creation-image-slot">
              <aside className="character-portrait-panel character-portrait-card creation-image-panel">
                <div className="character-portrait-frame">
                  {displayedPortrait?.src ? (
                    <MediaImage src={displayedPortrait.src} alt="Generated character portrait" size="display" />
                  ) : (
                    <span>{isGeneratingImage ? 'Generating portrait...' : 'No portrait generated yet'}</span>
                  )}
                </div>
                <div className="creation-image-counter">
                  <span>
                    {portraitCandidates.length > 0
                      ? `Image ${displayedPortraitIndex + 1} of ${portraitCandidates.length}`
                      : `0 of ${MAX_IMAGE_CANDIDATES}`}
                  </span>
                  <span>{displayedPortraitIsPrimary ? 'Primary image' : 'Not primary'}</span>
                </div>
                <div className="creation-image-nav">
                  <button type="button" onClick={() => setDisplayedPortraitIndex((current) => Math.max(0, current - 1))} disabled={displayedPortraitIndex <= 0}>
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setDisplayedPortraitIndex((current) => Math.min(portraitCandidates.length - 1, current + 1))}
                    disabled={displayedPortraitIndex >= portraitCandidates.length - 1}
                  >
                    Next
                  </button>
                </div>
                <div className="character-portrait-actions">
                  <button type="button" onClick={handleRegenerateImage} disabled={!canRegenerateImage}>
                    {isGeneratingImage ? 'Generating...' : 'Regenerate Image'}
                  </button>
                  <button type="button" onClick={handleSetPrimaryImage} disabled={!displayedPortrait || displayedPortraitIsPrimary || isSettingPrimaryImage}>
                    {isSettingPrimaryImage ? 'Saving...' : 'Save Image'}
                  </button>
                </div>
              </aside>
            </div>
          </div>

          <section className="character-sheet-section creation-backstory-section">
            <h2>Backstory</h2>
            {backstory?.backstory ? (
              <p>{backstory.backstory}</p>
            ) : (
              <div className="character-empty-state">{isGeneratingFamily ? 'Generating backstory...' : 'Backstory is not ready yet.'}</div>
            )}
          </section>

          {runError && <div className="character-sheet-status character-sheet-error">{runError}</div>}
          {Object.entries(generationErrors).filter(([, error]) => error).map(([step, error]) => {
            const busy = step === 'portrait' ? isGeneratingImage : isGeneratingFamily;
            const label = step === 'portrait' ? 'Image' : 'Background';
            return (
              <div key={step} className="character-sheet-status character-sheet-error" role="alert">
                <p>{error}</p>
                <button type="button" className="character-inline-button" disabled={busy || isSaving} onClick={() => {
                  setRunError('');
                  const runId = runIdRef.current;
                  if (step === 'portrait') generatePortraitCandidate(runId, { regenerate: portraitCandidates.length > 0 });
                  else if (step === 'background') runFamilyGeneration(runId, familyRolls, { ancestors, giftItems, backstory });
                }}>
                  {busy ? 'Retrying...' : `Retry ${label}`}
                </button>
              </div>
            );
          })}

          <div className="creation-nav-row">
            <button type="button" className="character-continue-button" onClick={() => setPage(1)} disabled={isFinalizing}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
              <span>Back</span>
            </button>
            <button type="button" className="character-continue-button" onClick={handleFinalContinue} disabled={!isPage2Ready || isSaving || isFinalizing}>
              <span>{isFinalizing ? 'Saving...' : 'Continue'}</span>
              <i className="bi bi-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="character-sheet-page character-creation-page">
      <section className="character-sheet-shell character-creation-shell">
        <header className="character-sheet-header">
          <div>
            <h1>Character Creation</h1>
            <p>{character.idEntity ? 'Saved draft' : 'New character draft'}</p>
          </div>
          <div className="character-level-pill">Level 1</div>
        </header>

        {isLoading && <div className="character-sheet-status">Loading character...</div>}
        {statusMessage && <div className="character-sheet-status">{statusMessage}</div>}
        {errorMessage && <div className="character-sheet-status character-sheet-error">{errorMessage}</div>}

        <section className="character-sheet-section">
          <h2>Identity</h2>
          <div className="character-sheet-grid creation-identity-grid">
            <label className="creation-identity-name">
              <HelpLabel label="Name" help="The character's personal name." />
              <input
                value={character.name ?? ''}
                placeholder="Required"
                required
                onChange={(event) => updateCharacter({ name: event.target.value, title: event.target.value })}
              />
            </label>
            <label className="creation-identity-form">
              <HelpLabel label="Form" help="The character's race.  Starting characters only have one form." />
              <select required value={character.beingType ?? ''} onChange={(event) => updateBeingType(event.target.value)}>
                <option value="">Select...</option>
                {characterBeingTypeOptions.map((option) => (
                  <option key={`character-creation-being-type-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="creation-age-field">
              <div className="creation-age-inline">
                <HelpLabel
                  label="Age"
                  help={'Starting age in years.\nDefaults:\nElf: 200-300\nDwarf/Gnome: 40-60\nHalfling: 32-48\nHuman: 18-24'}
                />
                <div className="character-point-row creation-age-stepper">
                  <button
                    type="button"
                    onClick={() => adjustAge(-1)}
                    disabled={!ageBounds || characterAge <= ageBounds.min}
                    aria-label="Decrease age"
                  >
                    -
                  </button>
                  <strong>{characterAge || '-'}</strong>
                  <button
                    type="button"
                    onClick={() => adjustAge(1)}
                    disabled={!ageBounds || characterAge >= ageBounds.max}
                    aria-label="Increase age"
                  >
                    +
                  </button>
                </div>
                <span className="creation-age-range">
                  {ageBounds ? `${ageBounds.min}-${ageBounds.max} years` : ''}
                </span>
              </div>
            </label>
            <label className="creation-identity-gender">
              <HelpLabel label="Gender" help="The character's gender presentation. This guides pronouns, family narration, and portrait details." />
              <select required value={character.gender ?? ''} onChange={(event) => updateCharacter({ gender: event.target.value })}>
                <option value="">Select...</option>
                {getMasterTypeOptions('gender').map((option) => (
                  <option key={`character-creation-gender-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </section>

        <section className="character-sheet-section">
          <div className="character-sheet-section-header">
            <h2>Stats</h2>
            <span>{statRemaining} points remaining</span>
          </div>
          <div className="character-point-grid">
            {STAT_FIELDS.map((stat) => {
              const current = numberValue(character[stat.key]);
              return (
                <div key={stat.key} className="character-point-row">
                  <span className="character-point-label">
                    <span>{stat.label}</span>
                    <em>{stat.help}</em>
                  </span>
                  <button type="button" onClick={() => adjustStat(stat.key, -1)} disabled={current <= STAT_MIN}>
                    -
                  </button>
                  <strong>{current}</strong>
                  <button type="button" onClick={() => adjustStat(stat.key, 1)} disabled={current >= STAT_MAX || statTotal >= STAT_POINT_TOTAL}>
                    +
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section className="character-sheet-section">
          <div className="character-sheet-section-header">
            <h2>Primary Abilities</h2>
            <span>{primarySkillRemaining} points remaining</span>
          </div>
          <div className="character-point-grid">
            {displaySkillRows.map((skill) => (
              <div key={skill.idPrimarySkillList} className="character-point-row">
                <span className="character-point-label">
                  <span>{skill.name}</span>
                  <em>{skill.description || PRIMARY_ABILITY_DESCRIPTIONS[Number(skill.idPrimarySkillList)]}</em>
                </span>
                <button type="button" onClick={() => adjustPrimarySkill(skill.idPrimarySkillList, -1)} disabled={skill.level <= PRIMARY_SKILL_MIN}>
                  -
                </button>
                <strong>{skill.level}</strong>
                <button type="button" onClick={() => adjustPrimarySkill(skill.idPrimarySkillList, 1)} disabled={skill.level >= PRIMARY_SKILL_MAX || primarySkillTotal >= PRIMARY_SKILL_POINT_TOTAL}>
                  +
                </button>
              </div>
            ))}
            {displaySkillRows.length === 0 && <div className="character-empty-state">No primary skills loaded.</div>}
          </div>
        </section>

        <section className="character-sheet-section">
          <h2>Details</h2>
          <div className="character-sheet-grid">
            <label>
              <HelpLabel label="Heraldry / Colors" help="Optional colors, symbols, family signs, badges, or visual motifs for the character and portrait." />
              <input
                value={character.heraldry ?? ''}
                placeholder="Optional"
                onChange={(event) => updateCharacter({ heraldry: event.target.value })}
              />
            </label>
            <label className="character-sheet-wide-field">
              <span className="character-field-label-row">
                <HelpLabel label="Description" help="Required visual and personality sketch. This guides the portrait, ancestors, and backstory." />
                <button
                  type="button"
                  className="character-inline-button"
                  onClick={onGenerateDescription}
                  disabled={isLoading || isSaving || isGeneratingDescription || typeof onGenerateDescription !== 'function'}
                >
                  {isGeneratingDescription ? 'Generating...' : 'Generate Description'}
                </button>
              </span>
              <textarea
                value={character.description ?? ''}
                placeholder="Required"
                required
                onChange={(event) => updateCharacter({ description: event.target.value })}
              />
            </label>
          </div>
        </section>

        {message && (
          <div className="character-sheet-status character-sheet-error creation-action-message" aria-live="polite">
            {message}
          </div>
        )}
        <div className="creation-nav-row creation-primary-nav">
          {isWaitingForWorld && <p className="character-sheet-status" role="status">Please wait, saving is waiting on the world copy to finish.</p>}
          {onExit && <button type="button" className="character-continue-button character-exit-button" onClick={onExit} disabled={isSaving}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
            <span>Your Characters</span>
          </button>}
          <button type="button" className="character-continue-button creation-next-button" onClick={handlePage1Continue} disabled={isLoading || isSaving}>
            <span>{isSaving ? 'Saving...' : 'Continue'}</span>
            {!isSaving && <i className="bi bi-arrow-right" aria-hidden="true" />}
          </button>
        </div>
      </section>
    </main>
  );
}

export default CharacterCreation;
